import React from 'react'
import styled from 'styled-components'
import { Checkbox } from '@progress/kendo-react-inputs'
import { t } from 'i18next'
import CustomFormInput from 'components/input/custom-form-input/CustomFormInput'
import { BUTTONS, FORM, PLACEHOLDERS, TABLE } from 'locales/locales'
import colors from 'utils/colors'
import { icons } from 'utils/icons'
import UploadImage from 'components/input/uplaod-input/UploadImage'
import { AvatarSize } from 'components/avatar/Avatar'

export interface InAppBrowser {
  name: string
  url: string
  isActive: boolean
  inAppBrowserLogo: string
  isExtraTab: boolean
  _id: string
}

interface InAppBrowserProps {
  browsers: InAppBrowser[]
  onAdd: () => void
  onRemove: (index: number) => void
  onChange: (index: number, field: keyof InAppBrowser, value: string | boolean) => void
  onChangeLogo: (fileUrl: string, index: number) => void
}

export const InAppBrowserSettings: React.FC<InAppBrowserProps> = ({
  browsers,
  onAdd,
  onRemove,
  onChange,
  onChangeLogo,
}) => {
  return (
    <StyledContainer>
      <StyledTableContainer>
        <StyledTable>
          <thead>
            <tr>
              <th>{t(FORM.LOGO)}</th>
              <th>{t(FORM.NAME)}</th>
              <th>URL</th>
              <th>{t(TABLE.TEAM_STATUS)}</th>
              <th>{t(TABLE.SHOW_IN_MENU)}</th>
              <th>{t(TABLE.TEAM_ACTIONS)}</th>
            </tr>
          </thead>
          <tbody>
            {browsers.map((browser, index) => (
              <tr key={index}>
                <StyledInputCell>
                  <UploadImage
                    type="inAppBrowser"
                    title={''}
                    avatar={browser.inAppBrowserLogo || icons.defaultGroupIcon.src}
                    buttonText={t(BUTTONS.UPLOAD)}
                    onUploadPhoto={(fileUrl) => onChangeLogo(fileUrl, index)}
                    avatarSize={AvatarSize.medium}
                    id={browser._id}
                  />
                </StyledInputCell>
                <StyledInputCell>
                  <CustomFormInput
                    value={browser.name}
                    onChange={(value) => onChange(index, 'name', value)}
                    type="text"
                    placeholder={t(PLACEHOLDERS.IN_APP_BORWSER_NAME)}
                    preset="small"
                    willError={false}
                  />
                </StyledInputCell>
                <StyledInputCell>
                  <CustomFormInput
                    value={browser.url}
                    onChange={(value) => onChange(index, 'url', value)}
                    type="text"
                    placeholder={t(PLACEHOLDERS.IN_APP_BORWSER_URL)}
                    preset="small"
                    willError={false}
                  />
                </StyledInputCell>
                <td>
                  <StyledCheckboxContainer>
                    <Checkbox
                      checked={browser.isActive}
                      onChange={() => onChange(index, 'isActive', !browser.isActive)}
                    />
                  </StyledCheckboxContainer>
                </td>
                <td>
                  <StyledCheckboxContainer>
                    <Checkbox
                      checked={browser.isExtraTab}
                      onChange={() => onChange(index, 'isExtraTab', !browser.isExtraTab)}
                    />
                  </StyledCheckboxContainer>
                </td>
                <td>
                  <StyledActions>
                    <StyledActionButton onClick={() => onRemove(index)} variant="danger">
                      {t(BUTTONS.REMOVE)}
                      <i className="fas fa-trash" />
                    </StyledActionButton>
                  </StyledActions>
                </td>
              </tr>
            ))}
          </tbody>
        </StyledTable>
      </StyledTableContainer>
      <StyledAddButton onClick={onAdd}>
        <PlusIcon /> {t(BUTTONS.ADD)}
      </StyledAddButton>
    </StyledContainer>
  )
}

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
  width: 100%;
`;

const StyledTableContainer = styled.div`
  max-height: 400px; 
  overflow-y: auto;
  overflow-x: auto;
  border-radius: 8px;
  border: 1px solid ${colors.light80};

  &::-webkit-scrollbar {
    width: 4px;
    height: 4px;
  }
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: white;
  table-layout: fixed;
  min-width: 650px;

  th {
    height: 48px;
    padding: 8px 16px;
    background: ${colors.light95};
    font-weight: 600;
    color: ${colors.vimeDark};
    overflow:hidden;
    word-wrap:break-word;
    text-overflow: ellipsis;
    border-bottom: 1px solid ${colors.light90};
    text-align: center;
    position: sticky; // Header'ı sabit tut
    top: 0; // En üstte sabitle
    z-index: 1; // Diğer içeriğin üzerinde göster
  }

  td {
    height: 72px;
    padding: 0 16px;
    border-bottom: 1px solid ${colors.light90};
    vertical-align: middle;
  }

  tbody {
    tr:nth-child(even) {
      background-color: ${colors.light80};
    }
    
    /* tr:hover {
      background-color: ${colors.light95};
    } */
  }

  // Sütun genişlikleri
  th:nth-child(1), td:nth-child(1) { width: 15%; }
  th:nth-child(2), td:nth-child(2) { width: 25%; }
  th:nth-child(3), td:nth-child(3) { 
    width: 25%; 
    text-align: center;
  }
  th:nth-child(4), td:nth-child(4) { 
    width: 12%; 
    text-align: center;
  }
  th:nth-child(5), td:nth-child(5) { 
    width: 12%; 
    text-align: center;
  }
  th:nth-child(6), td:nth-child(6) { 
    width: 15%; 
    text-align: center;
  }
`;

const StyledInputCell = styled.td`
  .custom-form-input {
    height: 40px;
    width: 100%;

    > div {
      height: 100%;
      margin: 0;
      transition: border-color 0.2s;
      
      &:hover {
        border-color: ${colors.light50};
      }
      
      &:focus-within {
        border-color: ${colors.primaryVime};
        box-shadow: 0 0 0 2px rgba(17, 159, 219, 0.1);
      }
    }

    input {
      height: 100%;
      padding: 0 12px;
      font-size: 14px;
      width: 100%;
    }

    > div:last-child {
      display: none;
    }
  }
`;

const StyledCheckboxContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 40px;

  .k-checkbox {
    margin: 0;
    width: 20px;
    height: 20px;

    &.k-checked {
      background-color: ${colors.primaryVime} !important;
      border-color: ${colors.primaryVime} !important;
    }

    &:focus {
      box-shadow: 0 0 0 2px rgba(17, 159, 219, 0.2);
    }
  }
`;

const StyledActions = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 40px;
  padding: 0 4px;
`;

const StyledActionButton = styled.button<{ variant?: 'danger' }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 6px 8px;
  min-width: 0;
  width: 100%;
  max-width: 100px;
  height: 36px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  background: ${(props) => (props.variant === 'danger' ? colors.red : colors.primaryVime)};
  color: white;
  transition: all 0.2s;
  font-size: 13px;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &:hover {
    background: ${(props) => (props.variant === 'danger' ? colors.red : colors.vimeDark)};
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }

  i {
    font-size: 13px;
    margin-left: 2px;
  }
`

const StyledAddButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 15px;
  padding: 12px 24px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  background: ${colors.primaryVime};
  color: white;
  transition: all 0.2s;
  font-weight: 500;

  &:hover {
    background: ${colors.vimeDark};
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`

const PlusIcon = styled.span`
  display: flex;
  align-items: center;
  line-height: 0;

  &:before {
    content: '+';
    font-size: 20px;
    font-weight: bold;
  }
`

export default InAppBrowserSettings